import React, { useEffect, useState } from "react";
import { getAllDocsSnap } from "../../utils/firebase/Firebase.utils";
import MobileWrapper from "../../components/MobileWrapper";
import { Link, useNavigate } from "react-router-dom";
import {
  FaChevronLeft,
  FaPlus,
  FaSearch,
  FaTimes,
  FaTimesCircle,
  FaCheckCircle,
  FaImage,
  FaTint,
  FaCalendarAlt,
  FaClock,
  FaHome,
  FaUser,
  FaCalendar,
  FaFilter,
} from "react-icons/fa";
import { unitNumberLabel } from "../../utils/constants";

export default function WaterLogs() {
  const [waterLogs, setWaterLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState("all"); // "all", "saved", "pending"
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [selectedYear, setSelectedYear] = useState("all");
  const navigate = useNavigate();

  // Get current year for filter options
  const currentYear = new Date().getFullYear();
  const yearOptions = ["all", currentYear, currentYear - 1, currentYear - 2];

  // Month options for filter
  const monthOptions = [
    { value: "all", label: "All Months" },
    { value: "0", label: "January" },
    { value: "1", label: "February" },
    { value: "2", label: "March" },
    { value: "3", label: "April" },
    { value: "4", label: "May" },
    { value: "5", label: "June" },
    { value: "6", label: "July" },
    { value: "7", label: "August" },
    { value: "8", label: "September" },
    { value: "9", label: "October" },
    { value: "10", label: "November" },
    { value: "11", label: "December" },
  ];

  useEffect(() => {
    // Subscribe to real-time updates from the meterReading collection
    const unsubscribe = getAllDocsSnap("meterReading", (data) => {
      // Sort by dateTime (newest first)
      const sortedLogs = data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setWaterLogs(sortedLogs);
      setLoading(false);
    });

    // Cleanup subscription on component unmount
    return () => unsubscribe && unsubscribe();
  }, []);

  const filteredLogs = waterLogs
    .filter(
      filter === "all"
        ? () => true
        : (log) => log.status.toLowerCase() === filter.toLowerCase()
    )
    .filter((log) => {
      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase().trim();
      const unitMatches = log.unitNumber?.toLowerCase().includes(query);
      const meterMatches = log.meterReading?.toLowerCase().includes(query);

      return unitMatches || meterMatches;
    })
    .filter((log) => {
      // Filter by year
      if (selectedYear === "all") return true;
      const logDate = new Date(log.dateTime);
      return logDate.getFullYear() === parseInt(selectedYear);
    })
    .filter((log) => {
      // Filter by month
      if (selectedMonth === "all") return true;
      const logDate = new Date(log.dateTime);
      return logDate.getMonth() === parseInt(selectedMonth);
    });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleOpenImage = (e, imageUrl) => {
    e.stopPropagation(); // Prevent event bubbling
    setSelectedImage(imageUrl);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedImage(null);
  };

  // Count for each status type
  const getStatusCount = (status) => {
    return waterLogs.filter(
      (log) => log.status?.toLowerCase() === status.toLowerCase()
    ).length;
  };

  // Reset all filters
  const resetFilters = () => {
    setFilter("all");
    setSelectedMonth("all");
    setSelectedYear("all");
    setSearchQuery("");
  };

  return (
    <MobileWrapper label="Water Meter Logs">
      <div className="w-full mx-auto px-4 max-w-4xl">
        {/* Header Section */}
        <div className="flex items-center justify-between mb-6 bg-[#363062]/5 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-[#363062] rounded-lg">
              <FaTint className="text-white text-base" />
            </div>
            <div>
              <h1 className="text-sm font-medium text-[#363062]">
                Water Meter Management
              </h1>
              <p className="text-xs text-gray-500">
                Track water meter readings and logs
              </p>
            </div>
          </div>
          <Link
            to="/water-meter-reading"
            className="flex items-center px-3 py-2 text-xs font-medium text-white rounded-md bg-[#8FCD00] shadow-sm hover:bg-[#7db700] transition-colors"
          >
            <FaPlus className="w-3 h-3 mr-1.5" />
            New
          </Link>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <FaSearch className="w-3.5 h-3.5 text-gray-500" />
          </div>
          <input
            type="text"
            className="block w-full p-2.5 pl-10 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062]"
            placeholder={`Search by ${unitNumberLabel} or meter reading...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ borderColor: "#E2E8F0" }}
          />
          {searchQuery && (
            <button
              className="absolute inset-y-0 right-0 flex items-center pr-3"
              onClick={() => setSearchQuery("")}
            >
              <FaTimesCircle className="w-3.5 h-3.5 text-gray-500" />
            </button>
          )}
        </div>

        {/* Date Filters */}
        <div className="mb-4 grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 ml-1">
              Month
            </label>
            <div className="relative">
              <select
                className="block w-full p-2 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062] pr-8 appearance-none"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{ borderColor: "#E2E8F0" }}
              >
                {monthOptions.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <FaCalendar className="w-3 h-3 text-gray-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1 ml-1">
              Year
            </label>
            <div className="relative">
              <select
                className="block w-full p-2 text-xs border rounded-lg bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#363062] focus:border-[#363062] pr-8 appearance-none"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                style={{ borderColor: "#E2E8F0" }}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year === "all" ? "All Years" : year}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <FaCalendar className="w-3 h-3 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Reset Filters Button */}
        {(filter !== "all" ||
          selectedMonth !== "all" ||
          selectedYear !== "all" ||
          searchQuery) && (
          <div className="mb-4">
            <button
              onClick={resetFilters}
              className="w-full flex items-center justify-center py-1.5 text-xs font-medium text-[#363062] bg-[#363062]/10 rounded-md hover:bg-[#363062]/20"
            >
              <FaFilter className="w-3 h-3 mr-1.5" />
              Reset All Filters
            </button>
          </div>
        )}

        {/* Status Filter Buttons */}
        <div className="mb-4 overflow-x-auto">
          <div className="flex space-x-2 pb-2">
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                filter === "all"
                  ? "bg-[#363062] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
              onClick={() => setFilter("all")}
            >
              All ({waterLogs.length})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                filter === "saved"
                  ? "bg-[#8FCD00] text-white"
                  : "bg-[#8FCD00]/10 text-gray-700 hover:bg-[#8FCD00]/20"
              }`}
              onClick={() => setFilter("saved")}
            >
              Saved ({getStatusCount("saved")})
            </button>
            <button
              className={`px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                filter === "pending"
                  ? "bg-[#4299E1] text-white"
                  : "bg-[#4299E1]/10 text-gray-700 hover:bg-[#4299E1]/20"
              }`}
              onClick={() => setFilter("pending")}
            >
              Pending ({getStatusCount("pending")})
            </button>
          </div>
        </div>

        {/* Water Logs Cards */}
        <div className="mt-4 space-y-3">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-[#363062]"></div>
                <p className="mt-2 text-sm text-[#363062]">
                  Loading water meter logs...
                </p>
              </div>
            </div>
          ) : filteredLogs.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-100">
              <div className="p-3 bg-[#363062]/10 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                <FaTint className="text-[#363062] text-lg" />
              </div>
              <h3 className="text-sm font-medium text-[#363062] mb-1">
                {waterLogs.length === 0 ? "No Logs" : "No Matching Results"}
              </h3>
              <p className="text-xs text-gray-500">
                {waterLogs.length === 0
                  ? "No water meter readings have been logged yet"
                  : "Try adjusting your filters or search terms"}
              </p>
            </div>
          ) : (
            filteredLogs.map((log) => (
              <div
                key={log.id}
                className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden transition-all hover:shadow-md"
              >
                <div className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div className="text-sm font-medium text-[#363062]">
                      {log.unitNumber
                        ? `Unit ${log.unitNumber}`
                        : "Unknown Unit"}
                    </div>
                    <div
                      className="flex items-center px-2 py-1 rounded-full text-xs font-medium gap-1.5"
                      style={{
                        backgroundColor:
                          log.status?.toLowerCase() === "saved"
                            ? "#8FCD0015"
                            : "#4299E115",
                        color:
                          log.status?.toLowerCase() === "saved"
                            ? "#8FCD00"
                            : "#4299E1",
                      }}
                    >
                      <span className="text-xs">
                        {log.status?.toLowerCase() === "saved" ? (
                          <FaCheckCircle />
                        ) : (
                          <FaClock />
                        )}
                      </span>
                      {log.status || "Unknown"}
                    </div>
                  </div>

                  <div className="flex items-center mb-2">
                    <div className="text-xs font-medium text-gray-600">
                      Meter Reading: {log.meterReading || "Not recorded"}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                    <div className="flex items-center gap-1.5">
                      <FaCalendarAlt className="text-gray-400 flex-shrink-0" />
                      <span>{formatDate(log.dateTime).split(",")[0]}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FaClock className="text-gray-400 flex-shrink-0" />
                      <span>{formatDate(log.dateTime).split(",")[1]}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <FaHome className="text-gray-400 flex-shrink-0" />
                      <span>Unit {log.unitNumber || "Unknown"}</span>
                    </div>

                    {log.createdBy && (
                      <div className="flex items-center gap-1.5">
                        <FaUser className="text-gray-400 flex-shrink-0" />
                        <span>{log.createdByName || log.createdBy}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions Section */}
                <div className="py-3 px-4 bg-gray-50 border-t border-gray-100">
                  <div className="flex flex-wrap justify-between items-center gap-2">
                    {/* Utility buttons */}
                    <div className="flex items-center gap-2">
                      {log.imageUrl && (
                        <button
                          className="px-2 py-1 text-xs text-[#363062] bg-[#363062]/10 rounded-md hover:bg-[#363062]/20 transition flex items-center"
                          onClick={(e) => handleOpenImage(e, log.imageUrl)}
                        >
                          <FaImage className="mr-1 w-3 h-3" />
                          View Photo
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Image Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-hidden relative">
              <button
                className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 p-2 z-10 bg-white bg-opacity-75 rounded-full"
                onClick={handleCloseModal}
              >
                <FaTimes className="h-6 w-6" />
              </button>
              <div className="p-1 flex items-center justify-center overflow-auto max-h-[80vh]">
                <img
                  src={selectedImage}
                  alt="Water Meter"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </MobileWrapper>
  );
}
